import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { Loader2, Mail, Send } from "lucide-react";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { useLeadModal } from "@/context/LeadModalContext";
import { getAttribution } from "@/lib/attribution";

const EMAIL_REGEX = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

export default function LeadFormModal() {
  const { isOpen, closeModal } = useLeadModal();
  const navigate = useNavigate();
  const [email, setEmail] = useState("");
  const [error, setError] = useState("");
  const [isSubmitting, setIsSubmitting] = useState(false);

  const resetAndClose = () => {
    setError("");
    setEmail("");
    closeModal();
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (isSubmitting) return;

    const trimmed = email.trim();
    if (!EMAIL_REGEX.test(trimmed)) {
      setError("Inserisci un indirizzo email valido.");
      return;
    }

    setError("");
    setIsSubmitting(true);
    try {
      const res = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/lead`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ email: trimmed, ...getAttribution() }),
      });

      if (!res.ok) {
        throw new Error("lead-request-failed");
      }

      if (typeof window !== "undefined" && typeof window.fbq === "function") {
        window.fbq("track", "Lead");
      }

      setEmail("");
      closeModal();
      navigate("/thank-you");
    } catch (err) {
      setError("Non siamo riusciti a inviare la richiesta. Riprova tra qualche istante.");
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <Dialog open={isOpen} onOpenChange={(open) => !open && resetAndClose()}>
      <DialogContent
        data-testid="lead-modal"
        className="max-w-md rounded-3xl border border-yellow-400/25 bg-[#0a0a0a] p-6 text-white shadow-[0_0_60px_rgba(255,215,0,0.15)] sm:p-8"
      >
        <DialogHeader>
          <DialogTitle
            data-testid="lead-modal-title"
            className="font-heading text-2xl font-black uppercase tracking-tight text-white"
          >
            Accedi ad <span className="text-gradient-gold">Aurum AI</span>
          </DialogTitle>
          <DialogDescription
            data-testid="lead-modal-description"
            className="font-body text-sm text-gray-400"
          >
            Lascia la tua email per ricevere la guida gratuita e accedere alla community Telegram.
          </DialogDescription>
        </DialogHeader>

        <form onSubmit={handleSubmit} className="mt-2 space-y-4" data-testid="lead-modal-form" noValidate>
          <div className="relative">
            <Mail className="pointer-events-none absolute left-3.5 top-1/2 h-4 w-4 -translate-y-1/2 text-gray-500" />
            <Input
              type="email"
              inputMode="email"
              autoComplete="email"
              placeholder="tuaemail@esempio.com"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              disabled={isSubmitting}
              data-testid="lead-modal-email-input"
              aria-label="Indirizzo email"
              aria-invalid={!!error}
              className="h-12 rounded-xl border-white/10 bg-black/40 pl-10 text-white placeholder:text-gray-500 focus-visible:ring-yellow-400"
            />
          </div>

          {error && (
            <p
              data-testid="lead-modal-error"
              role="alert"
              className="font-body text-sm text-red-400"
            >
              {error}
            </p>
          )}

          <button
            type="submit"
            disabled={isSubmitting}
            data-testid="lead-modal-submit-button"
            className="group inline-flex w-full items-center justify-center gap-2 rounded-full bg-gradient-to-r from-yellow-300 via-yellow-400 to-yellow-600 px-6 py-4 font-heading text-sm font-black uppercase tracking-wider text-black shadow-[0_0_30px_rgba(255,215,0,0.45)] transition-all duration-300 hover:scale-[1.02] hover:shadow-[0_0_50px_rgba(255,215,0,0.7)] disabled:cursor-not-allowed disabled:opacity-60 disabled:hover:scale-100"
          >
            {isSubmitting ? (
              <>
                <Loader2 className="h-4 w-4 animate-spin" />
                Invio in corso...
              </>
            ) : (
              <>
                <Send className="h-4 w-4 transition-transform group-hover:translate-x-1" />
                Ricevi accesso gratuito
              </>
            )}
          </button>

          <p
            data-testid="lead-modal-privacy"
            className="font-body text-[11px] leading-relaxed text-gray-500"
          >
            Rispettiamo la tua privacy: la tua email verrà utilizzata solo per inviarti la guida gratuita e le comunicazioni relative al servizio.
          </p>
        </form>
      </DialogContent>
    </Dialog>
  );
}
